import graphql__716c4743c84a61de8505 from "./__generated__/MapFilterElement_filter.graphql.js";import { useCallback, useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import castArray from "lodash/castArray";
import includes from "lodash/includes";

import { useMapContext } from "../MapContext";
import Select from "../../Select/Select";

import {
  initialActiveIdsFor,
  initializeFilterSelection,
  updateElementFilter
} from "../../../reducers/map/filters";

const MapFilterElement = props => {
  const filter = useFragment(MapFilterElement_filter, props.filter);
  const {
    id: filterId,
    name,
    mapElements,
    filterElementsOutOnInitialize,
    allowSelectingMultipleElements
  } = filter;
  const { mapKey } = useMapContext();

  const dispatch = useDispatch();

  const selectableElementIds = useMemo(
    () => mapElements.map(({ id }) => id),
    [mapElements]
  );

  const initialActiveElementIds = useMemo(
    () => initialActiveIdsFor(filterElementsOutOnInitialize, selectableElementIds),
    [filterElementsOutOnInitialize, selectableElementIds]
  );

  useEffect(() => {
    dispatch(
      initializeFilterSelection(mapKey, filterId, {
        activeElementIds: initialActiveElementIds
      })
    );
  }, [dispatch, filterId, initialActiveElementIds, mapKey]);

  // We don't default the value to an empty array here, as returning a new array reference
  // on each call would re-render this component on every dispatched action.
  const activeElementIds = useSelector(
    ({ map }) => map[mapKey].filters.byFilterId[filterId]?.activeElementIds
  );

  const selectOptions = useMemo(() => {
    return mapElements.map(({ id, name }) => ({
      label: name,
      value: id
    }));
  }, [mapElements]);

  const selectedOptions = useMemo(() => {
    const activeIds = activeElementIds || initialActiveElementIds;

    return selectOptions.filter(({ value }) => includes(activeIds, value));
  }, [activeElementIds, initialActiveElementIds, selectOptions]);

  const selectValue = useMemo(() => {
    if (allowSelectingMultipleElements) return selectedOptions;

    // A single selection control can't display the whole set of elements a filter shows
    // when it's not configured to hide them on initialize, so it only displays a value
    // once the visitor has actually narrowed the filter down to one element.
    return selectedOptions.length === 1 ? selectedOptions[0] : null;
  }, [allowSelectingMultipleElements, selectedOptions]);

  const handleChanged = useCallback(
    selection => {
      // React Select passes an array of options when the filter allows selecting
      // multiple elements, but a single option object — or null once the selection is
      // cleared — when it doesn't, so we always normalize it to an array of options.
      const changedOptions = castArray(selection || []);
      const selectedElementIds = changedOptions.map(({ value }) => value);

      dispatch(updateElementFilter(mapKey, filterId, selectedElementIds));
    },
    [dispatch, filterId, mapKey]
  );

  return (
    <div className="map-filter map-filter-element">
      <Select
        options={selectOptions}
        value={selectValue}
        isMulti={allowSelectingMultipleElements}
        // Clearing the select is what turns the filter off and hides its elements, so it
        // has to stay reachable even when only one element can be selected at a time.
        isClearable={true}
        onChange={handleChanged}
        placeholder={name}
      />
    </div>
  );
};

const MapFilterElement_filter = graphql__716c4743c84a61de8505;

export default MapFilterElement;
